/**
 * Saving a CSV the backend streamed back as a blob.
 *
 * Two things bite every time this is written inline (GZ#2904):
 *
 * 1. The server picks the filename (Content-Disposition), because only it
 *    knows the event name and date. Parsing it wrong silently saves the file
 *    as "download" or as the last URL segment.
 * 2. With `responseType: "blob"` axios hands back the ERROR body as a blob
 *    too, so `error.response.data.message` is undefined and the user gets a
 *    generic failure toast instead of "Service booking not found".
 */

/**
 * Pull the filename out of a Content-Disposition header.
 * Handles `filename="a b.csv"`, bare `filename=a.csv`, and RFC 5987
 * `filename*=UTF-8''a%20b.csv` (which wins when both are present).
 */
export const filenameFromContentDisposition = (
    header: string | null | undefined,
    fallback: string
): string => {
    if (!header) return fallback;

    const extended = /filename\*\s*=\s*([^']*)'[^']*'([^;]+)/i.exec(header);
    if (extended) {
        try {
            const decoded = decodeURIComponent(extended[2].trim());
            if (decoded) return decoded;
        } catch {
            // Malformed percent-encoding — fall through to the plain filename.
        }
    }

    const plain = /filename\s*=\s*("([^"]*)"|[^;]+)/i.exec(header);
    if (plain) {
        const value = (plain[2] ?? plain[1] ?? "").trim();
        if (value) return value;
    }

    return fallback;
};

/** Hand the blob to the browser as a download. */
export const saveCsvBlob = (blob: Blob | BlobPart, filename: string) => {
    const url = URL.createObjectURL(
        new Blob([blob as BlobPart], { type: "text/csv;charset=utf-8;" })
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

/**
 * Recover the API's error message from a failed blob-response request, so a
 * 404/403 still tells the admin what happened.
 */
export const messageFromBlobError = async (
    error: any,
    fallback: string
): Promise<string> => {
    const body = error?.response?.data;
    if (body && typeof body.text === "function") {
        try {
            const parsed = JSON.parse(await body.text());
            if (parsed?.message) return String(parsed.message);
        } catch {
            // Not JSON (an HTML error page, say) — use the fallback.
        }
    }
    if (typeof body?.message === "string") return body.message;
    return fallback;
};
